import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Apple, Bell, CheckCircle, Loader2, Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePlatform } from "@/hooks/usePlatform";
import { APP_STORE_URL, getDeviceLabel, trackCtaClick } from "@/data/appLinks";
import phoneShot from "@/assets/app-phone-screenshot.png";

interface AppStoreCTAProps {
  /** Where this CTA lives, e.g. "airport_hero" — used for click analytics. */
  placement: string;
  /** Extra context such as the airport code or blog slug. */
  context?: string;
  headline?: string;
  subline?: string;
  variant?: "card" | "inline";
  /** Hide the phone screenshot in tight spots. */
  showArtwork?: boolean;
}

export const AppStoreCTA = ({
  placement,
  context,
  headline = "Get alerts when this line gets long",
  subline = "Airport Buddy for iPhone — free. Live TSA waits, leave-by timer, and gate-change alerts.",
  variant = "card",
  showArtwork = true,
}: AppStoreCTAProps) => {
  const { isNative } = usePlatform();
  const [device, setDevice] = useState<string>("desktop");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setDevice(getDeviceLabel());
  }, []);

  // Never advertise the App Store inside the native app itself.
  if (isNative) return null;

  const isIOS = device === "ios";
  const isAndroid = device === "android";

  const handleStoreClick = () => trackCtaClick(placement, context);

  const submitEmail = async (source: string) => {
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase
        .from("email_subscribers" as any)
        .insert({ email: trimmed, source } as any);
      if (error && (error as any).code !== "23505") throw error;

      if (!isAndroid) {
        // Deliver the download link for people who can actually install it.
        const { error: sendError } = await supabase.functions.invoke("send-app-link", {
          body: { email: trimmed, source: placement },
        });
        if (sendError) throw sendError;
      }

      setSubmitted(true);
      trackCtaClick(isAndroid ? "android_waitlist" : `${placement}_email`, context);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const emailForm = (buttonLabel: string, source: string, placeholder = "you@example.com") => (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submitEmail(source);
      }}
      className="flex flex-col sm:flex-row gap-2 max-w-md"
    >
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={placeholder}
        maxLength={255}
        required
        aria-label="Email address"
        className="min-h-[44px] text-base rounded-xl"
      />
      <Button
        type="submit"
        disabled={loading}
        className="min-h-[44px] px-6 rounded-xl active:scale-95 whitespace-nowrap"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : buttonLabel}
      </Button>
    </form>
  );

  const storeBadge = (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleStoreClick}
      className="inline-flex items-center gap-3 rounded-xl bg-foreground px-5 py-3 min-h-[48px] text-background shadow-md active:scale-95 transition-transform"
      aria-label="Download Airport Buddy on the App Store"
    >
      <Apple className="h-7 w-7 shrink-0" aria-hidden="true" />
      <span className="flex flex-col leading-tight text-left">
        <span className="text-[11px] uppercase tracking-wide opacity-80">Download on the</span>
        <span className="text-lg font-semibold">App Store</span>
      </span>
    </a>
  );

  const rating = (
    <div className="flex items-center gap-1.5 text-base text-muted-foreground">
      <span className="flex" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} className="h-4 w-4 fill-warning text-warning" />
        ))}
      </span>
      <span>Free · No account needed</span>
    </div>
  );

  const action = submitted ? (
    <div className="flex items-center gap-2 text-base text-foreground">
      <CheckCircle className="h-5 w-5 text-primary" aria-hidden="true" />
      {isAndroid
        ? "You're on the list — we'll email you when Android is ready."
        : "Sent! Check your inbox for the download link."}
    </div>
  ) : isIOS ? (
    <div className="space-y-3">
      {storeBadge}
      {rating}
    </div>
  ) : isAndroid ? (
    <div className="space-y-3">
      {emailForm("Notify me", "android_waitlist")}
      <p className="text-sm text-muted-foreground">
        Airport Buddy is on iPhone today. Leave your email and we'll tell you the day Android lands.
      </p>
      <p className="text-sm text-muted-foreground">
        In the meantime: tap your browser menu and choose <strong>Add to Home screen</strong> to keep this
        page one tap away.
      </p>
    </div>
  ) : (
    <div className="space-y-3">
      {emailForm("Send me the app", `app_link_${placement}`)}
      <a
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleStoreClick}
        className="inline-flex items-center gap-1.5 text-base text-primary hover:underline"
      >
        <Apple className="h-4 w-4" aria-hidden="true" />
        Or open it on the App Store
      </a>
    </div>
  );

  const resolvedHeadline = isAndroid ? "Want an alert when this line spikes?" : headline;
  const resolvedSubline = isAndroid
    ? "The iPhone app does it automatically. Android is next — get notified at launch."
    : subline;

  const body = (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-lg font-bold text-foreground">{resolvedHeadline}</p>
        <p className="text-base text-muted-foreground">{resolvedSubline}</p>
      </div>
      {action}
    </div>
  );

  if (variant === "inline") return body;

  return (
    <Card className="rounded-xl shadow-md border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5 overflow-hidden">
      <CardContent className="py-6">
        <div className="flex items-start gap-4">
          {showArtwork && !isAndroid ? (
            <img
              src={phoneShot}
              alt="Airport Buddy app showing a live TSA wait time and leave-by time"
              width={640}
              height={1024}
              loading="lazy"
              className="hidden sm:block w-24 h-auto shrink-0 -my-2"
            />
          ) : (
            <span className="hidden sm:inline-flex p-3 rounded-xl bg-primary/10 text-primary shrink-0">
              <Bell className="h-6 w-6" aria-hidden="true" />
            </span>
          )}
          <div className="flex-1 min-w-0">{body}</div>
        </div>
      </CardContent>
    </Card>
  );
};
